import { AsyncPipe, DatePipe, LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterLink } from '@angular/router';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { CurrentSubscriptionService } from '@rma/billing/subscription-plans/public/api-subscription-features';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { LoadingIndicatorDirective } from '@rma/generic/ui/loading-indicator';
import { fromControl } from '@rma/generic/util/operators/from-control';
import { SubmissionFormFactoryService } from '@rma/generic/util/typed-forms';
import { exhaustMap, map, Subject, take } from 'rxjs';

import { UnsubscribeThankyouComponent } from '../ui-unsubscribe-thankyou/unsubscribe-thankyou.component';

@Component({
  selector: 'rma-unsubscribe-complete',
  imports: [
    RmaIconComponent,
    TranslocoModule,
    MatCardModule,
    MatButtonModule,
    LoadingIndicatorDirective,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    AsyncPipe,
    LowerCasePipe,
    DatePipe,
    MatOptionModule,
    MatSelectModule,
    RouterLink,
    UnsubscribeThankyouComponent,
  ],
  templateUrl: './unsubscribe-complete.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideTranslocoScope('unsubscribeComplete')],
})
export class UnsubscribeCompleteComponent {
  private readonly submissionFormFactoryService = inject(SubmissionFormFactoryService);
  private readonly subscriptionService = inject(CurrentSubscriptionService);
  private readonly router = inject(Router);

  protected readonly currentSubscriptionEnds$ = this.subscriptionService.currentSubscription$.pipe(map((x) => x.subscriptionEnd));
  protected readonly reasons$ = this.subscriptionService.likedReasons$;
  protected readonly unSubscriptionCompleteForm = this.createSubmissionForm();
  protected readonly isSubmitting$ = this.unSubscriptionCompleteForm.submitting$;
  protected readonly error$ = this.unSubscriptionCompleteForm.onError$.pipe(
    map((e) => e.errors.map((a) => a.errorMessage).reduce((curr, accum) => `${accum} ${curr}`)),
  );

  protected readonly isOtherSelected$ = fromControl(this.unSubscriptionCompleteForm.controls.likedReason).pipe(map((r) => r === 'Other'));
  protected readonly displayThankYou$ = new Subject<boolean>();

  public constructor() {
    this.unSubscriptionCompleteForm.onSuccess$.pipe(take(1)).subscribe(() => {
      this.displayThankYou$.next(true);
    });
  }

  private createSubmissionForm() {
    const { fb } = this.submissionFormFactoryService;

    const form = fb.group({
      likedReason: fb.control(null, [Validators.required]),
      likedReasonText: fb.control(''),
    });

    return this.submissionFormFactoryService.createInferredSubmissionForm(
      form,
      exhaustMap((formData) => {
        const likedReasons = formData.likedReason ? [formData.likedReason] : [];
        return this.subscriptionService.postLikedReason(likedReasons, formData.likedReasonText);
      }),
    );
  }
}
